import React, { useEffect, useState } from "react";
import { useModulesBackupStore, useRouterStore } from "../../../stores";
import {
  Dropdown,
  MenuProps,
  Avatar,
  message,
  Badge,
  Tooltip,
  Modal,
  Button,
  Alert,
} from "antd";
import {
  CloseCircleOutlined,
  ReloadOutlined,
  SettingOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DatabaseOutlined,
  InfoCircleOutlined, // Añadido para el gestor de cola
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { DialogCloseSession } from "../../../../root/components/dialog";
import "../../css/user-avatar.css";
import { Person, SyncLock } from "@mui/icons-material";
import { getAllModulesByProfileId } from "../../../apis";
import {
  triggerSync,
  getPendingSyncCount,
  onSyncCompleted,
  checkForUpdates,
  forceUpdateAndReload,
} from "../../../helpers/";
import { SyncQueueManager } from "./modal-list-sync"; // Importar el componente de cola
import IndexedDBManagerModal from "./index-db-manager"; // Ajusta la ruta según tu estructura
import { ErrorBoundary } from "./error-boundary";

export const UserAvatar = () => {
  const navigate = useNavigate();
  const firstName = useRouterStore((state) => state.firstName);
  const avatar = useRouterStore((state) => state.avatar);
  const [openSession, setOpenSession] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [pendingItems, setPendingItems] = useState(0);
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  // Añadir estado para el gestor de cola
  const [queueModalVisible, setQueueModalVisible] = useState(false);
  // Añadir estado para controlar el menú desplegable
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [indexedDBManagerVisible, setIndexedDBManagerVisible] =
    useState<boolean>(false);
  const [swVersion, setSwVersion] = useState<string>("Cargando...");

  // Verificar elementos pendientes de sincronización
  useEffect(() => {
    const checkPending = async () => {
      try {
        const count = await getPendingSyncCount();
        setPendingItems(count);
      } catch (err) {
        console.error("Error al verificar elementos pendientes:", err);
      }
    };

    // Comprobar elementos pendientes al inicio
    checkPending();

    // Y después cada 30 segundos
    const interval = setInterval(checkPending, 30000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    setSwVersion(process.env.REACT_APP_VERSION || "v1.0.0");
  }, []);

  // Escuchar eventos de sincronización completada
  useEffect(() => {
    const cleanup = onSyncCompleted(({ timestamp }) => {
      setIsSyncing(false);
      setLastSyncTime(timestamp);
      setPendingItems(0);
      message.success("Sincronización completada correctamente");
    });

    return cleanup;
  }, []);

  // Escuchar mensajes de actualización del SW
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === "NEW_VERSION_AVAILABLE") {
        setUpdateAvailable(true);
      }
    };

    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener("message", handleMessage);
    }

    return () => {
      if ("serviceWorker" in navigator) {
        navigator.serviceWorker.removeEventListener("message", handleMessage);
      }
    };
  }, []);

  // Manejar cambios de estado de conexión
  useEffect(() => {
    const handleOnline = () => {
      if (pendingItems > 0) {
        message.info(
          "Conexión restablecida. Sincronizando datos pendientes..."
        );
        handleSyncData();
      }
    };

    window.addEventListener("online", handleOnline);

    return () => {
      window.removeEventListener("online", handleOnline);
    };
  }, [pendingItems]);

  const handleReloadModules = async (e: React.MouseEvent) => {
    // Prevenir cierre del dropdown
    e.stopPropagation();

    try {
      await reloadModules();
    } catch (error) {
      console.error("Error al recargar módulos:", error);
    }
  };

  const handleOpenQueueManager = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevenir cierre del dropdown

    // Si estamos en modo offline, mostrar una advertencia
    if (!navigator.onLine) {
      message.warning(
        "Estás en modo offline. Funcionalidad limitada disponible."
      );
    }

    try {
      setQueueModalVisible(true);
      setDropdownVisible(false); // Cerrar el dropdown para evitar problemas de UI
    } catch (error) {
      console.error("Error al abrir el gestor de cola:", error);
      message.error("No se pudo abrir el gestor de cola");
    }
  };

  const handleSyncData = async (e?: React.MouseEvent) => {
    // Prevenir cierre del dropdown si se proporciona el evento
    if (e) {
      e.stopPropagation();
    }

    if (isSyncing) return;

    setIsSyncing(true);
    try {
      const result = await triggerSync();

      if (!result.success) {
        message.warning(result.message);
        setIsSyncing(false);
      } else {
        // Añadir un timeout de seguridad para detener el spinner después de 10 segundos
        setTimeout(() => {
          setIsSyncing(false);
        }, 10000);
      }
    } catch (err) {
      console.error("Error al iniciar sincronización:", err);
      message.error("Error al sincronizar datos");
      setIsSyncing(false);
    }
  };

  const handleCheckUpdates = async (e: React.MouseEvent) => {
    // Prevenir cierre del dropdown
    e.stopPropagation();

    message.loading("Verificando actualizaciones...");
    await checkForUpdates();
    setTimeout(() => {
      if (!updateAvailable) {
        message.success("La aplicación está actualizada");
      }
    }, 2000);
  };

  const handleUpdate = async (e: React.MouseEvent) => {
    // Prevenir cierre del dropdown
    e.stopPropagation();

    if (isUpdating) return;

    setIsUpdating(true);
    message.loading("Verificando actualizaciones...");

    try {
      await forceUpdateAndReload();
      // No reseteamos isUpdating porque la página se recargará
    } catch (err) {
      console.error("Error al actualizar:", err);
      setIsUpdating(false);
      message.error("Error al aplicar la actualización");
    }
  };

  // Manejar el cierre del modal de cola
  const handleQueueModalClose = () => {
    setQueueModalVisible(false);
    // Actualizar el contador después de cerrar
    getPendingSyncCount().then((count) => setPendingItems(count));
  };

  // Formatear última fecha de sincronización
  const formattedLastSync = lastSyncTime
    ? `Última sincronización: ${lastSyncTime.toLocaleTimeString()}`
    : "No se ha sincronizado aún";

  const items: MenuProps["items"] = [
    {
      style: {
        backgroundColor: "white",
        color: "#191970",
        margin: "5px",
        borderRadius: "4px",
        overflow: "hidden",
      },
      label: (
        <div
          onClick={(e) => {
            e.stopPropagation(); // Prevenir cierre del dropdown
            navigate("/user/information");
            setDropdownVisible(false); // Cerrar manualmente tras navegar
          }}
          style={{
            padding: "8px 12px",
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
          }}
        >
          <div style={{ marginRight: "8px" }}>
            <Person style={{ fontSize: "18px", color: "#191970" }} />
          </div>
          <div
            style={{
              maxWidth: "calc(100% - 26px)",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <div
              style={{
                fontSize: "14px",
                fontWeight: "bold",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {firstName}
            </div>
            <div
              style={{
                fontSize: "12px",
                color: "#666",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              Ver perfil
            </div>
          </div>
        </div>
      ),
      key: `option-one-avatar`,
      icon: null, // El icono está ahora dentro del label para mejor control
    },
    {
      style: {
        backgroundColor: "#3CB371",
        color: "white",
        margin: "5px",
        textAlign: "left",
      },
      label: (
        <p
          onClick={handleReloadModules}
          style={{
            fontSize: "14px",
            fontWeight: "bold",
            margin: "0px",
          }}
        >
          Recargar Módulos
        </p>
      ),
      key: `option-reload-modules`,
      icon: <SyncLock />,
    },
    {
      style: {
        backgroundColor: "#4169E1",
        color: "white",
        margin: "5px",
        textAlign: "left",
      },
      label: (
        <Tooltip title={formattedLastSync} placement="left">
          <div
            onClick={isSyncing ? undefined : (e) => handleSyncData(e)} // Pasar el evento
            style={{
              fontSize: "14px",
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              opacity: isSyncing ? 0.7 : 1,
              cursor: isSyncing ? "not-allowed" : "pointer",
            }}
          >
            <span>Sincronizar</span>
            {isSyncing ? (
              <SyncOutlined spin style={{ marginLeft: 5 }} />
            ) : pendingItems > 0 ? (
              <Badge count={pendingItems} size="small" offset={[0, 0]} />
            ) : lastSyncTime ? (
              <CheckCircleOutlined
                style={{ color: "#52c41a", marginLeft: 5 }}
              />
            ) : null}
          </div>
        </Tooltip>
      ),
      key: `option-sync-data`,
      icon: <SyncOutlined spin={isSyncing} />,
    },
    // Añadir nuevo elemento para el gestor de cola
    {
      style: {
        backgroundColor: "#8A2BE2", // Violeta
        color: "white",
        margin: "5px",
        textAlign: "left",
      },
      label: (
        <div
          onClick={handleOpenQueueManager} // Usar el nuevo manejador
          style={{
            fontSize: "14px",
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span>Gestionar Cola</span>
          {pendingItems > 0 && (
            <Badge count={pendingItems} size="small" offset={[0, 0]} />
          )}
        </div>
      ),
      key: `option-queue-manager`,
      icon: <ClockCircleOutlined />,
    },
    {
      style: {
        backgroundColor: updateAvailable ? "#fa8c16" : "#708090", // Naranja si hay actualización
        color: "white",
        margin: "5px",
        textAlign: "left",
      },
      label: (
        <div
          onClick={updateAvailable ? handleUpdate : handleCheckUpdates} // Pasar el evento
          style={{
            fontSize: "14px",
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            opacity: isUpdating ? 0.7 : 1,
            cursor: isUpdating ? "not-allowed" : "pointer",
          }}
        >
          <span>{updateAvailable ? "Actualizar" : "Check Update"}</span>
          {updateAvailable && !isUpdating && (
            <Badge dot offset={[0, 0]} style={{ marginLeft: 5 }} />
          )}
        </div>
      ),
      key: `option-check-update`,
      icon: <ReloadOutlined spin={isUpdating} />,
    },
    {
      style: {
        backgroundColor: "#808000", // Color púrpura para distinguirlo
        color: "white",
        margin: "5px",
        textAlign: "left",
      },
      label: (
        <p
          onClick={(e) => {
            e.stopPropagation(); // Prevenir cierre del dropdown
            setIndexedDBManagerVisible(true);
            setDropdownVisible(false); // Cerrar manualmente
          }}
          style={{
            fontSize: "14px",
            fontWeight: "bold",
            margin: "0px",
          }}
        >
          Multimedia
        </p>
      ),
      key: "option-indexeddb-manager",
      icon: <DatabaseOutlined />,
    },
    {
      style: {
        backgroundColor: "crimson",
        color: "white",
        margin: "5px",
        textAlign: "left",
      },
      label: (
        <p
          onClick={(e) => {
            e.stopPropagation(); // Prevenir cierre del dropdown
            setOpenSession(true);
            setDropdownVisible(false); // Cerrar manualmente
          }}
          style={{
            fontSize: "14px",
            fontWeight: "bold",
            margin: "0px",
          }}
        >
          Cerrar Sesión
        </p>
      ),
      key: "option-three-avatar",
      icon: <CloseCircleOutlined />,
    },
    {
      style: {
        backgroundColor: "#f0f0f0",
        color: "#666",
        margin: "5px",
        textAlign: "center",
        cursor: "default",
      },
      label: (
        <div
          style={{
            fontSize: "12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "4px",
          }}
        >
          <InfoCircleOutlined /> Versión: {swVersion}
        </div>
      ),
      key: "sw-version-info",
      disabled: true,
    },
  ];

  const handleCloseSession = React.useCallback(() => {
    setOpenSession(false);
  }, []);

  // Manejar apertura/cierre del dropdown
  const handleDropdownVisibleChange = (visible: boolean) => {
    setDropdownVisible(visible);
  };

  return (
    <div>
      <DialogCloseSession open={openSession} handleClose={handleCloseSession} />

      {/* Componente del gestor de cola */}
      {queueModalVisible && (
        <ErrorBoundary
          fallback={
            <Modal
              title="Error al cargar el gestor de cola"
              open={true}
              onCancel={() => setQueueModalVisible(false)}
              footer={[
                <Button key="close" onClick={() => setQueueModalVisible(false)}>
                  Cerrar
                </Button>,
              ]}
            >
              <Alert
                message="Error inesperado"
                description="Ocurrió un error al intentar cargar el gestor de cola. Esto puede deberse a problemas con la base de datos local o la conexión."
                type="error"
                showIcon
              />
              <div style={{ marginTop: 16 }}>
                <p>Sugerencias:</p>
                <ul>
                  <li>Verifica tu conexión a internet</li>
                  <li>Actualiza la página</li>
                  <li>Limpia la caché del navegador si el problema persiste</li>
                </ul>
              </div>
            </Modal>
          }
        >
          <SyncQueueManager
            visible={queueModalVisible}
            onClose={handleQueueModalClose}
            onSync={handleSyncData}
          />
        </ErrorBoundary>
      )}
      <IndexedDBManagerModal
        visible={indexedDBManagerVisible}
        onClose={() => setIndexedDBManagerVisible(false)}
      />
      <Dropdown
        destroyPopupOnHide
        menu={{ items }}
        trigger={["click"]}
        overlayStyle={{ width: 210, margin: "5px" }}
        open={dropdownVisible}
        onOpenChange={handleDropdownVisibleChange}
      >
        <Badge
          dot={pendingItems > 0 || updateAvailable}
          offset={[-5, 5]}
          color={
            updateAvailable ? "#fa8c16" : pendingItems > 0 ? "red" : undefined
          }
        >
          <Avatar className="avatar-element" size="large" gap={4} src={avatar}>
            {firstName?.toString().charAt(0) ?? ""}
          </Avatar>
        </Badge>
      </Dropdown>
    </div>
  );
};

export const reloadModules = async () => {
  const { setModules } = useRouterStore.getState();
  const { modulesBackup, setModulesBackup } = useModulesBackupStore.getState();

  if (navigator.onLine) {
    try {
      const tokenData = JSON.parse(
        localStorage.getItem(process.env.REACT_APP_TOKEN!) || "{}"
      );
      const token = tokenData.token; // Ajusta según la estructura de tu token
      const profileId = tokenData.user?.profileId;

      if (!token || !profileId) {
        throw new Error("No se encontró un token o profileId");
      }

      const { data: dataModules } = await getAllModulesByProfileId(profileId);
      if (dataModules) {
        setModules(dataModules); // Actualizar módulos en useRouterStore
        setModulesBackup(dataModules); // Actualizar backup
        message.success("Módulos recargados exitosamente");
        return dataModules;
      } else {
        throw new Error("No se encontraron módulos");
      }
    } catch (error: any) {
      if (!navigator.onLine || error.response?.status === 401) {
        if (modulesBackup.length > 0) {
          setModules(modulesBackup);
          message.warning("No hay conexión. Módulos cargados desde el backup.");
          return modulesBackup;
        }
        message.error("No hay conexión y no hay backup disponible.");
        throw new Error("No hay módulos disponibles");
      }
      message.error("Error al recargar módulos.");
      throw error;
    }
  } else {
    if (modulesBackup.length > 0) {
      setModules(modulesBackup);
      message.warning("No hay conexión. Módulos cargados desde el backup.");
      return modulesBackup;
    }
    message.error("No hay conexión y no hay backup disponible.");
    throw new Error("No hay módulos disponibles");
  }
};
